import { showToast } from '@purple-ui/components';
import { ApiError } from './api.ts';
import { escapeHtml, formData, html, raw, type Raw } from './html.ts';
import { t } from './i18n.ts';

export function toast(title: string, message?: string): void {
  showToast({ title, message, duration: 3600, compact: true });
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const unmet = (error.details as { unmet?: string[] } | undefined)?.unmet;
    if (unmet?.length) return `${error.message}: ${unmet.join(', ')}`;
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

export function toastError(error: unknown): void {
  showToast({ title: t('Aktion fehlgeschlagen'), message: errorMessage(error), duration: 6000, dismissible: true });
}

type Tone = 'success' | 'warning' | 'danger' | 'info' | '';

export function badge(label: string, tone: Tone = ''): Raw {
  return html`<span class="p-badge${tone ? ` p-badge--${tone}` : ''}">${label}</span>`;
}

const TONES: Record<string, Tone> = {
  // tasks
  todo: '', in_progress: 'info', blocked: 'danger', done: 'success', cancelled: '',
  // projects
  proposed: 'info', active: 'success', on_hold: 'warning', completed: '',
  // health
  green: 'success', amber: 'warning', red: 'danger',
  // risks
  open: 'warning', mitigating: 'info', accepted: '', closed: 'success', occurred: 'danger',
  // decisions / gates
  approved: 'success', rejected: 'danger', superseded: '', pending: 'info', passed: 'success', failed: 'danger', waived: 'warning',
  // priority
  low: '', medium: '', high: 'warning', urgent: 'danger',
};

export function statusBadge(value: string, labels: Record<string, string>): Raw {
  return badge(labels[value] ?? value, TONES[value] ?? '');
}

export function riskTone(score: number): Tone {
  if (score >= 15) return 'danger';
  if (score >= 8) return 'warning';
  return 'success';
}

export function emptyState(title: string, message: string, action?: Raw): Raw {
  return html`<section class="p-empty-state" data-p-size="compact">
    <div class="p-empty-state__content"><h3>${title}</h3><p>${message}</p></div>
    ${action ? html`<div class="p-empty-state__actions">${action}</div>` : ''}
  </section>`;
}

export function loading(label = t('Wird geladen …')): Raw {
  return html`<div class="pma-loading" role="status"><span class="p-spinner" aria-hidden="true"></span> ${label}</div>`;
}

export interface FormDialogOptions {
  title: string;
  description?: string;
  fields: Raw;
  submitLabel?: string;
  danger?: boolean;
  wide?: boolean;
  /** Return false to keep the dialog open without an error. */
  onSubmit: (data: Record<string, unknown>, form: HTMLFormElement) => Promise<unknown>;
  onOpen?: (dialog: HTMLDialogElement) => void;
}

let dialogCounter = 0;

/** Native modal dialog with a form; errors stay visible inside the dialog. */
export function openFormDialog(options: FormDialogOptions): HTMLDialogElement {
  const id = `pma-dialog-${++dialogCounter}`;
  const dialog = document.createElement('dialog');
  dialog.className = `p-dialog pma-dialog${options.wide ? ' pma-dialog--wide' : ''}`;
  dialog.setAttribute('aria-labelledby', `${id}-title`);
  dialog.innerHTML = html`<form method="dialog" class="p-dialog__body p-stack" data-p-gap="4" novalidate>
      <header><h2 id="${id}-title" class="pma-dialog__title">${options.title}</h2>
      ${options.description ? html`<p class="pma-muted">${options.description}</p>` : ''}</header>
      <div class="pma-form-grid">${options.fields}</div>
      <div class="p-alert p-alert--danger" role="alert" hidden data-error><span class="p-alert__icon" aria-hidden="true">!</span><div data-error-text></div></div>
      <div class="p-dialog__actions">
        <button type="button" class="p-button p-button--ghost" data-cancel>${t('Abbrechen')}</button>
        <button type="submit" class="p-button ${options.danger ? 'p-button--danger' : 'p-button--primary'}">${options.submitLabel ?? t('Speichern')}</button>
      </div>
    </form>`.value;
  document.body.append(dialog);
  const form = dialog.querySelector('form')!;
  const errorBox = dialog.querySelector<HTMLElement>('[data-error]')!;
  const submit = dialog.querySelector<HTMLButtonElement>('button[type="submit"]')!;
  dialog.querySelector('[data-cancel]')!.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', () => dialog.remove());
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    errorBox.hidden = true;
    try {
      const result = await options.onSubmit(formData(form), form);
      if (result !== false) dialog.close();
    } catch (error) {
      errorBox.querySelector('[data-error-text]')!.textContent = errorMessage(error);
      errorBox.hidden = false;
    } finally {
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  });
  dialog.showModal();
  options.onOpen?.(dialog);
  (form.querySelector('input, select, textarea') as HTMLElement | null)?.focus();
  return dialog;
}

export function confirmDialog(title: string, message: string, confirmLabel = t('Löschen')): Promise<boolean> {
  return new Promise((resolve) => {
    let confirmed = false;
    const dialog = openFormDialog({
      title,
      fields: html`<p class="pma-span-2">${message}</p>`,
      submitLabel: confirmLabel,
      danger: true,
      onSubmit: async () => {
        confirmed = true;
      },
    });
    dialog.addEventListener('close', () => resolve(confirmed));
  });
}

// ---------------------------------------------------------------- form fields

interface FieldOptions {
  required?: boolean;
  value?: unknown;
  hint?: string;
  span?: boolean;
  placeholder?: string;
  attrs?: string;
}

export function field(label: string, name: string, type = 'text', o: FieldOptions = {}): Raw {
  const value = o.value === null || o.value === undefined ? '' : String(o.value);
  return html`<label class="p-field${o.span ? ' pma-span-2' : ''}">
    <span class="p-label">${label}${o.required ? raw(' <span aria-hidden="true">*</span>') : ''}</span>
    <input class="p-input" type="${type}" name="${name}" value="${value}"${o.required ? raw(' required') : ''}${
      o.placeholder ? raw(` placeholder="${escapeHtml(o.placeholder)}"`) : ''
    }${o.attrs ? raw(` ${o.attrs}`) : ''}>
    ${o.hint ? html`<span class="p-hint">${o.hint}</span>` : ''}
  </label>`;
}

export function textarea(label: string, name: string, o: FieldOptions & { rows?: number } = {}): Raw {
  return html`<label class="p-field pma-span-2">
    <span class="p-label">${label}</span>
    <textarea class="p-textarea" name="${name}" rows="${o.rows ?? 4}"${o.required ? raw(' required') : ''}>${o.value ?? ''}</textarea>
    ${o.hint ? html`<span class="p-hint">${o.hint}</span>` : ''}
  </label>`;
}

export function select(label: string, name: string, optionsHtml: string, o: FieldOptions = {}): Raw {
  return html`<label class="p-field${o.span ? ' pma-span-2' : ''}">
    <span class="p-label">${label}${o.required ? raw(' <span aria-hidden="true">*</span>') : ''}</span>
    <select class="p-select" name="${name}"${o.required ? raw(' required') : ''}>${raw(optionsHtml)}</select>
    ${o.hint ? html`<span class="p-hint">${o.hint}</span>` : ''}
  </label>`;
}

export function optionList(items: { id: string; label: string }[], selected?: unknown, empty = '–'): string {
  const escape = escapeHtml;
  return (
    `<option value="">${escape(empty)}</option>` +
    items.map((i) => `<option value="${escape(i.id)}"${i.id === selected ? ' selected' : ''}>${escape(i.label)}</option>`).join('')
  );
}
